import {useMemo} from 'react';
import {useEntity} from '../contexts/entity/context';
import {CaregiverMode} from '../enums';
import {useFetchAllItems} from './fetch-all-items';
import {EntitySocialResponsesRepository} from '../repositories/entity-social-responses';
import {getLocalizedFieldValue} from '../utils/functions/get-localized-field-value';

/**
 * Hook to get the social responses of an entity.
 * Depending on caregiver mode:
 *  A) System Administrator: fetch social responses from the backend
 *  B) Professional: use social responses from the SELECTED entity context
 */
export function useEntitySocialResponses(opts: {
  entityId: string | undefined;
  caregiverMode: CaregiverMode;
}): {key: string; label: string}[] | undefined {
  const {state: entityState} = useEntity();

  const {items: entitySocialResponses} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return EntitySocialResponsesRepository.instance.getEntitySocialResponses(
        opts.entityId,
        limit,
        skip
      );
    },
    deps: [opts.entityId],
    enabled: CaregiverMode.SystemAdministrator === opts.caregiverMode,
  });

  const entitySocialResponseOptions = useMemo(() => {
    // A) System Administrator: social responses fetched from the backend
    if (opts.caregiverMode === CaregiverMode.SystemAdministrator) {
      return entitySocialResponses?.map((r) => {
        return {
          key: r.id,
          label: getLocalizedFieldValue(r, 'abbreviation'),
        };
      });
    }

    // B) Caregiver: social responses from the SELECTED entity context
    if (opts.caregiverMode === CaregiverMode.Professional) {
      return entityState.availableSocialResponses?.map((r) => {
        return {
          key: r.id,
          label: getLocalizedFieldValue(r, 'abbreviation'),
        };
      });
    }

    return undefined;
  }, [entityState.availableSocialResponses, entitySocialResponses, opts.caregiverMode]);

  return entitySocialResponseOptions;
}
